import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Heading } from '../../components/Heading';
import { useGetDraftArticleByIdQuery } from '../../hooks/queries/useGetDraftArticleByIdQuery';
import {
  ArticleDraftRequest,
  ArticleRequest,
  Label,
  UserRoles,
} from '../../api/queries';
import classes from './ContentSystemPage.module.css';
import { Button } from '../../components/Button';
import { CreateArticleDialog } from '../../components/CreateArticle/CreateArticleDialog/CreateArticleDialog';
import { CreateArticleForm } from '../../components/CreateArticle/CreateArticleForm/CreateArticleForm';
import { ArticleUtils } from '../../utils/ArticleUtils/ArticleUtils';
import { Alert } from '../../components/Alert/Alert';
import { Paragraph } from '../../components/Paragraph';
import { GhostLoader } from '../../components/GhostLoader';
import { Spinner } from '../../components/Spinner';
import { useEditorState } from 'reactjs-tiptap-editor';
import { useGetUserinfo } from '../../hooks/queries/useGetUserinfo';
import { useUpdateDraftArticle } from '../../hooks/useUpdateDraftArticle/useUpdateDraftArticle';

export const ContentSystemPage = ({ mode }: { mode: 'create' | 'edit' }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { editor } = useEditorState();
  const navigate = useNavigate();
  const isCreating = mode === 'create';
  const [tempUpdatedAt, setTempUpdatedAt] = useState<Date | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<Array<Label>>([]);
  const [openFinalPublishModal, setOpenFinalPublishModal] =
    useState<boolean>(false);

  const { data: userInfo } = useGetUserinfo();

  const isSuperAdmin: boolean =
    userInfo?.userRoles.includes(UserRoles.SUPER_ADMIN) || false;
  const isContentCreator: boolean =
    userInfo?.userRoles.includes(UserRoles.CONTENT_CREATOR) || false;

  const isEditing = mode === 'edit' && id !== undefined && isContentCreator;

  const { guide, setGuide } = useOutletContext<{
    guide: ArticleDraftRequest;
    setGuide: React.Dispatch<React.SetStateAction<ArticleDraftRequest>>;
  }>();

  const {
    data: draftArticle,
    isLoading: isDraftArticleLoading,
    error: draftArticleError,
  } = useGetDraftArticleByIdQuery(Number(id), isEditing);

  useEffect(() => {
    if (isCreating) {
      setGuide(ArticleUtils.resetArticle());
    } else if (isEditing && draftArticle && isContentCreator) {
      setGuide((prev) => ({
        ...prev,
        title:
          typeof draftArticle.title === 'string'
            ? draftArticle.title.trim()
            : prev.title,
        coverImageUrl: draftArticle.coverImageUrl ?? prev.coverImageUrl,
        coverImageAlt: draftArticle.coverImageAlt ?? prev.coverImageAlt,
        jsonContent: draftArticle.jsonContent ?? prev.jsonContent,
        isGlobalGuide: draftArticle.isGlobalGuide ?? prev.isGlobalGuide,
        labels: draftArticle.labels ?? prev.labels,
        updatedAt: draftArticle.updatedAt ?? prev.updatedAt,
        guideId: Number(draftArticle.guideId),
        draftId: Number(id),
      }));
    }
  }, [draftArticle, isEditing, setGuide, id, isContentCreator, isCreating]);

  const { updateDraftArticle, isUpdatePending, isCreateArticlePending } =
    useUpdateDraftArticle({
      mode,
      guide,
      setGuide,
      id,
      setTempUpdatedAt,
    });

  if (!isContentCreator) {
    return (
      <div className={classes.container}>
        <Alert title={t('contentSystemPageAccess.invalidAccessTitle')} variant="danger" size="lg">
          <Paragraph size="md">
            Du har ikke tilgang til å lage/redigere artikler.
          </Paragraph>
        </Alert>
      </div>
    );
  }

  if (isDraftArticleLoading || isCreateArticlePending) {
    return (
      <div className={classes.container}>
        <GhostLoader
          aria-busy="true"
          style={{ width: '100%', maxWidth: 580, height: 80 }}
        />
        <div className={classes.loaderCards}>
          <GhostLoader aria-busy="true" style={{ width: 240, height: 80 }} />
          <GhostLoader
            aria-busy="true"
            style={{ width: '100%', height: 300 }}
          />
          <GhostLoader aria-busy="true" style={{ width: 80, height: 50 }} />
        </div>
      </div>
    );
  }

  if (draftArticleError) {
    return (
      <div className={classes.container}>
        <Alert title={draftArticleError.name} variant="danger" size="lg">
          <Paragraph size="md">{draftArticleError.message}</Paragraph>
        </Alert>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Heading as="h1">
          {mode === 'edit'
            ? `(Utkast) Rediger artikkel - "${guide.title}"`
            : 'Opprett artikkel'}
        </Heading>
        <div className={classes.subHeader}>
          <Heading as="h4">Artikkelen lagres automatisk underveis.</Heading>
          <Heading as="h5">
            Utkastet lagres automatisk etter 2 sekunder etter endringene.
          </Heading>
        </div>
      </div>

      <CreateArticleForm
        article={guide}
        updateDraftArticle={updateDraftArticle}
        mode={mode}
        isSuperAdmin={isSuperAdmin}
      />

      <div className={classes.buttons}>
        <Button
          type="button"
          variant="secondary"
          disabled={isUpdatePending}
          size="medium"
          onClick={() => {
            navigate('/content-system/articles');
          }}
          className={classes.publishButton}
          aria-label={t('contentSystemPage.backToOverview')}
        >
          {t('contentSystemPage.backToOverview')}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="medium"
          disabled={
            ArticleUtils.isJsonContentEmpty(guide.jsonContent) ||
            draftArticle?.title?.length === 0 ||
            isDraftArticleLoading ||
            isCreateArticlePending ||
            isCreating ||
            isUpdatePending
          }
          onClick={() => {
            editor?.commands.blur();
            setOpenFinalPublishModal(true);
          }}
          className={classes.publishButton}
        >
          {isUpdatePending ? (
            <>
              <Spinner color="var(--ds-color-accent-base-contrast-default)" size={17} /> {t('contentSystemPage.publishing')}
            </>
          ) : (
            t('contentSystemPage.publish')
          )}
        </Button>
      </div>

      {guide && openFinalPublishModal && (
        <CreateArticleDialog
          isOpen={openFinalPublishModal}
          setIsOpen={setOpenFinalPublishModal}
          updateDraftArticle={updateDraftArticle}
          article={guide}
          setArticle={setGuide}
          setCoverImage={setCoverImage}
          coverImage={coverImage}
          selectedLabels={selectedLabels}
          setSelectedLabels={setSelectedLabels}
        />
      )}

      {(tempUpdatedAt || guide.updatedAt) && !isCreating && (
        <div className={classes.lastUpdatedAt}>
          <Heading as="h4">
            Autolagret:{' '}
            {ArticleUtils.updatedAtDateString(tempUpdatedAt ?? guide.updatedAt)}
          </Heading>
        </div>
      )}
    </div>
  );
};
